import {
  Users, UserPlus, Target, Bell, UploadSimple, UserSwitch,
  Funnel, Hand, Sliders, ChartBar, ListChecks, ClockCounterClockwise,
  CurrencyInr, Receipt, CreditCard, ChartPie, BellRinging, FileText,
  Buildings, ShieldCheck, UsersThree, ChartLine, GitBranch, GearSix,
  ChartLineUp, Trophy, FunnelSimple, Coins, CalendarBlank, Export,
  ChatCircle, WhatsappLogo, Envelope, ChatDots, Timer, BookOpen,
  Lightning, Robot, UserCirclePlus, Repeat, Megaphone, TreeStructure,
} from '@phosphor-icons/react';

const FEATURES = {
  'lead-management': {
    slug: 'lead-management',
    title: 'Lead Management',
    docsLink: '/docs/lead-management/overview',
    headline: 'Capture every inquiry.\nConvert more students.',
    subtitle: 'From Meta ads to walk-ins — capture, score, and nurture leads from 10+ sources with parent-student dual-contact workflows built specifically for Indian education.',
    color: '#2B6EF0',
    screenshot: '/screenshots/leads.png',
    seo: {
      title: 'Lead Management for Coaching Classes — YuHasPro CRM',
      description: 'Capture student leads from Meta, Google, WhatsApp & walk-ins. Auto-assign to counselors, score leads, and track follow-ups. Built for coaching institutes in India.',
    },
    capabilities: [
      { icon: UserPlus, title: 'Multi-Source Capture', desc: 'Ingest leads from Meta Ads, Google Ads, WhatsApp, website forms, walk-ins, referrals, and Just Dial — all into one unified pipeline.' },
      { icon: Users, title: 'Parent-Student Profiles', desc: 'Dual-contact records with parent phone, student details, board, target exam, and preferred batch timing — designed for Indian education.' },
      { icon: Target, title: 'Lead Scoring', desc: 'Automatically prioritize leads based on source quality, engagement, and stage progression. Focus your counselors on high-intent inquiries.' },
      { icon: UserSwitch, title: 'Smart Assignment', desc: 'Round-robin or rule-based lead assignment to counselors. Balance workloads automatically across your team.' },
      { icon: Bell, title: 'Follow-Up Reminders', desc: 'Never miss a callback. Automated follow-up alerts with overdue tracking so no lead falls through the cracks.' },
      { icon: UploadSimple, title: 'Bulk Import & Export', desc: 'Import leads from Excel, CSV, or Google Sheets. Export filtered lists for campaigns. Zero manual data entry.' },
    ],
    steps: [
      { num: '01', title: 'Capture', desc: 'Leads flow in automatically from your ad campaigns, website, WhatsApp, and walk-in register.' },
      { num: '02', title: 'Qualify', desc: 'Score and filter leads. Auto-assign to the right counselor based on branch, course, or source.' },
      { num: '03', title: 'Convert', desc: 'Track every touchpoint. Automated reminders ensure timely follow-ups until the student enrolls.' },
    ],
    metrics: [
      { value: '10+', label: 'Lead Sources' },
      { value: '3x', label: 'Faster Response' },
      { value: '40%', label: 'More Conversions' },
    ],
    relatedFeatures: ['admissions-pipeline', 'communication', 'automation'],
  },

  'admissions-pipeline': {
    slug: 'admissions-pipeline',
    title: 'Admissions Pipeline',
    docsLink: '/docs/admissions-pipeline/overview',
    headline: 'See your entire funnel.\nMove students forward.',
    subtitle: 'A visual Kanban board built for admissions — drag leads through inquiry, demo, application, fee payment, and enrollment. Every stage visible at a glance.',
    color: '#8B5CF6',
    screenshot: '/screenshots/pipeline.png',
    seo: {
      title: 'Visual Admissions Pipeline & Kanban Board — YuHasPro CRM',
      description: 'Drag-and-drop Kanban pipeline for student admissions. Track leads through inquiry to enrollment with stage-wise analytics. Built for coaching institutes.',
    },
    capabilities: [
      { icon: Hand, title: 'Drag & Drop Kanban', desc: 'Move leads between stages with a simple drag. Visual columns for each step — from new inquiry to enrolled.' },
      { icon: Funnel, title: 'Custom Stages', desc: 'Configure your pipeline to match your process. Add, rename, or reorder stages like demo_scheduled, fee_pending, or application.' },
      { icon: ChartBar, title: 'Stage Analytics', desc: 'See conversion rates between each stage. Identify where leads stall and optimize your funnel.' },
      { icon: ListChecks, title: 'Bulk Stage Actions', desc: 'Select multiple leads and move them together. Mass-update stages after a batch demo or event.' },
      { icon: ClockCounterClockwise, title: 'Activity Timeline', desc: 'Every lead shows a complete history — calls, emails, stage changes, notes — so any counselor can pick up the context instantly.' },
      { icon: Sliders, title: 'Pipeline Filters', desc: 'Filter by source, counselor, branch, date range, or course. Slice the pipeline any way you need.' },
    ],
    steps: [
      { num: '01', title: 'Visualize', desc: 'See every lead in your funnel across all stages. No more spreadsheet hunting.' },
      { num: '02', title: 'Progress', desc: 'Drag cards forward as students complete demos, submit applications, and pay fees.' },
      { num: '03', title: 'Analyze', desc: 'Stage-wise metrics reveal bottlenecks. Know exactly where your funnel leaks.' },
    ],
    metrics: [
      { value: '10', label: 'Pipeline Stages' },
      { value: '60%', label: 'Less Manual Work' },
      { value: '2x', label: 'Pipeline Velocity' },
    ],
    relatedFeatures: ['lead-management', 'analytics', 'fee-collection'],
  },

  'fee-collection': {
    slug: 'fee-collection',
    title: 'Fee Collection',
    docsLink: '/docs/fee-collection/overview',
    headline: 'Invoicing that\nactually works.',
    subtitle: 'Generate invoices, track partial payments, manage UPI/cash/bank transactions, and see real-time outstanding dashboards per student — all without spreadsheets.',
    color: '#E5A100',
    screenshot: '/screenshots/fees.png',
    seo: {
      title: 'Fee Collection & Invoice Management for Institutes — YuHasPro',
      description: 'Generate invoices, track partial payments via UPI, cash & bank transfer. Real-time outstanding dashboards for coaching classes and schools in India.',
    },
    capabilities: [
      { icon: Receipt, title: 'Invoice Generation', desc: 'Create professional invoices with course details, discounts, and tax breakdowns. Send via WhatsApp or email in one click.' },
      { icon: CurrencyInr, title: 'Partial Payments', desc: 'Accept installments. Track paid vs pending amounts per invoice. Parents pay in parts, you see the full picture.' },
      { icon: CreditCard, title: 'Multi-Mode Tracking', desc: 'Record payments via UPI, cash, cheque, bank transfer, or card. Every mode tracked with timestamp and reference ID.' },
      { icon: ChartPie, title: 'Outstanding Dashboard', desc: 'Real-time view of who owes what. Filter by branch, course, or date. Stop chasing — start collecting.' },
      { icon: BellRinging, title: 'Payment Reminders', desc: 'Automated WhatsApp and SMS reminders for upcoming and overdue payments. Configurable frequency and templates.' },
      { icon: FileText, title: 'Receipt & Reports', desc: 'Auto-generate payment receipts. Export fee reports by branch, period, or payment mode for accounting.' },
    ],
    steps: [
      { num: '01', title: 'Invoice', desc: 'Generate invoices for enrolled students — course fees, registration, materials — with applicable discounts.' },
      { num: '02', title: 'Collect', desc: 'Accept payments via any mode. Record partial payments and track installment schedules.' },
      { num: '03', title: 'Reconcile', desc: 'Real-time dashboards show collected vs outstanding. Export for your accountant.' },
    ],
    metrics: [
      { value: '85%', label: 'Collection Rate' },
      { value: '5x', label: 'Faster Invoicing' },
      { value: '0', label: 'Revenue Leakage' },
    ],
    relatedFeatures: ['analytics', 'multi-branch', 'automation'],
  },

  'multi-branch': {
    slug: 'multi-branch',
    title: 'Multi-Branch Ops',
    docsLink: '/docs/multi-branch/overview',
    headline: 'One dashboard.\nEvery centre.',
    subtitle: 'Manage 1 centre or 100 franchises from a single control panel. Role-based access, branch-level analytics, and centralised settings — scale without chaos.',
    color: '#10A882',
    screenshot: '/screenshots/dashboard.png',
    seo: {
      title: 'Multi-Branch Management for Education Franchises — YuHasPro',
      description: 'Manage multiple coaching centres from one dashboard. Branch-level analytics, role-based access, and centralised control for education franchises in India.',
    },
    capabilities: [
      { icon: Buildings, title: 'Centralized Control', desc: 'Add branches, configure courses, set fee structures — all from one admin panel. Changes propagate instantly.' },
      { icon: ChartLine, title: 'Branch Analytics', desc: 'Compare performance across branches. Revenue, enrollments, lead conversion — broken down by centre.' },
      { icon: ShieldCheck, title: 'Role-Based Access', desc: 'Admins see everything. Branch managers see their branch. Counselors see their leads. Granular RBAC built in.' },
      { icon: UsersThree, title: 'Team Management', desc: 'Add staff, assign roles, transfer between branches. See who\'s active, their workload, and performance.' },
      { icon: GitBranch, title: 'Cross-Branch Transfers', desc: 'Transfer leads between branches when a student prefers a different location. Full history preserved.' },
      { icon: GearSix, title: 'Unified Settings', desc: 'Global pipeline stages, fee templates, and automation rules that apply across all branches. Override per-branch when needed.' },
    ],
    steps: [
      { num: '01', title: 'Setup', desc: 'Add your branches with address, courses, and staff. Configure role permissions for each level.' },
      { num: '02', title: 'Operate', desc: 'Each branch runs independently with its own pipeline, fees, and team — all under your central view.' },
      { num: '03', title: 'Scale', desc: 'Add new branches in minutes. Clone settings from existing ones. Franchise-ready from day one.' },
    ],
    metrics: [
      { value: '100+', label: 'Branches Supported' },
      { value: '5', label: 'Role Levels' },
      { value: '1', label: 'Dashboard' },
    ],
    relatedFeatures: ['analytics', 'fee-collection', 'lead-management'],
  },

  analytics: {
    slug: 'analytics',
    title: 'Analytics & Reports',
    docsLink: '/docs/analytics/overview',
    headline: 'Data-driven decisions.\nNot gut feelings.',
    subtitle: 'Source ROI, counselor leaderboards, conversion funnels, revenue tracking — every metric you need to run admissions like a science, not an art.',
    color: '#E04B2A',
    screenshot: '/screenshots/reports.png',
    seo: {
      title: 'Admissions Analytics & Reports for Coaching Classes — YuHasPro',
      description: 'Track source ROI, counselor performance, conversion funnels, and revenue. Data-driven admissions analytics for coaching institutes in India.',
    },
    capabilities: [
      { icon: ChartLineUp, title: 'Source ROI', desc: 'Know exactly which ad campaign, referral source, or event brings the best students. Spend where it counts.' },
      { icon: Trophy, title: 'Counselor Leaderboards', desc: 'Rank your team by conversions, response time, and follow-up rate. Celebrate top performers, coach the rest.' },
      { icon: FunnelSimple, title: 'Conversion Funnels', desc: 'Visualize drop-off at every stage. See where leads stall and take action before they go cold.' },
      { icon: Coins, title: 'Revenue Tracking', desc: 'Real-time revenue by branch, course, period, and payment mode. Know your numbers without waiting for month-end.' },
      { icon: CalendarBlank, title: 'Custom Date Ranges', desc: 'Compare this month vs last. This quarter vs same quarter last year. Any range, any metric.' },
      { icon: Export, title: 'Export Everything', desc: 'One-click PDF and CSV exports for every report. Feed your accountant, board presentation, or franchise review.' },
    ],
    steps: [
      { num: '01', title: 'Collect', desc: 'Every action in YuHasPro generates data — lead created, stage changed, payment received, message sent.' },
      { num: '02', title: 'Visualize', desc: 'Pre-built dashboards show KPIs at a glance. Drill down into any metric for the full story.' },
      { num: '03', title: 'Act', desc: 'Spot trends, reallocate budgets, reward top counselors, fix leaky stages — all backed by real numbers.' },
    ],
    metrics: [
      { value: '15+', label: 'Report Types' },
      { value: 'Real-time', label: 'Data Refresh' },
      { value: '1-click', label: 'Export' },
    ],
    relatedFeatures: ['admissions-pipeline', 'fee-collection', 'multi-branch'],
  },

  communication: {
    slug: 'communication',
    title: 'Communication Hub',
    docsLink: '/docs/communication/overview',
    headline: 'WhatsApp. SMS. Email.\nOne inbox.',
    subtitle: 'Unified messaging with templates, bulk sends, and conversation history. Follow up faster, never lose context, and reach parents on their preferred channel.',
    color: '#06B6D4',
    screenshot: '/screenshots/communication.png',
    seo: {
      title: 'WhatsApp, SMS & Email Communication for Institutes — YuHasPro',
      description: 'Unified messaging hub with WhatsApp integration, SMS campaigns, and email templates. Reach parents and students from one CRM. Built for coaching classes.',
    },
    capabilities: [
      { icon: WhatsappLogo, title: 'WhatsApp Integration', desc: 'Send messages directly from the CRM via WhatsApp Business. QR pairing, templates, and delivery tracking built in.' },
      { icon: ChatCircle, title: 'SMS Campaigns', desc: 'Bulk SMS for fee reminders, batch schedules, and event invites. DLT-compliant templates for Indian telecom.' },
      { icon: Envelope, title: 'Email Templates', desc: 'Professional email templates for welcome, follow-up, admission confirmation, and fee receipt. Customizable per branch.' },
      { icon: ChatDots, title: 'Unified Inbox', desc: 'See every message sent to a lead — WhatsApp, SMS, email — in one timeline. Any counselor can continue the conversation.' },
      { icon: Timer, title: 'Scheduled Messages', desc: 'Queue messages for the right time. Morning follow-ups, weekend reminders, batch start notifications — scheduled and sent automatically.' },
      { icon: BookOpen, title: 'Template Library', desc: 'Pre-built message templates for every stage. Inquiry acknowledgment, demo invite, fee reminder, enrollment confirmation.' },
    ],
    steps: [
      { num: '01', title: 'Connect', desc: 'Pair your WhatsApp Business number via QR code. Configure SMS and email credentials.' },
      { num: '02', title: 'Compose', desc: 'Pick a template or write a custom message. Send to one lead or broadcast to a filtered segment.' },
      { num: '03', title: 'Track', desc: 'See delivery status, read receipts, and response history. Every message logged on the lead timeline.' },
    ],
    metrics: [
      { value: '3', label: 'Channels' },
      { value: '95%', label: 'Delivery Rate' },
      { value: '50+', label: 'Templates' },
    ],
    relatedFeatures: ['lead-management', 'automation', 'admissions-pipeline'],
  },

  automation: {
    slug: 'automation',
    title: 'Automation Engine',
    docsLink: '/docs/automation/overview',
    headline: 'Set the rules.\nLet it run.',
    subtitle: 'Trigger-based workflows that auto-assign leads, send reminders, update stages, and notify your team — hands-free. Build once, scale forever.',
    color: '#F97316',
    screenshot: '/screenshots/automations.png',
    seo: {
      title: 'Admissions Automation & Workflows for Coaching Classes — YuHasPro',
      description: 'Automate lead assignment, follow-up reminders, stage updates, and notifications. Trigger-based workflows for coaching institutes. No coding required.',
    },
    capabilities: [
      { icon: Lightning, title: 'Trigger Workflows', desc: 'When a lead is created, stage changes, or payment is received — trigger any action automatically. If-this-then-that logic.' },
      { icon: UserCirclePlus, title: 'Auto Lead Assignment', desc: 'New leads assigned instantly based on source, branch, course, or round-robin rules. Zero manual distribution.' },
      { icon: Robot, title: 'Smart Reminders', desc: 'Automatic follow-up reminders when leads go cold. Escalation alerts when SLAs are breached.' },
      { icon: Repeat, title: 'Stage Automation', desc: 'Auto-move leads to the next stage when conditions are met. Demo attended? Move to application. Fee paid? Move to enrolled.' },
      { icon: Megaphone, title: 'Team Notifications', desc: 'Notify counselors via in-app alerts, email, or WhatsApp when leads are assigned, overdue, or need attention.' },
      { icon: TreeStructure, title: 'Workflow Templates', desc: 'Pre-built automation recipes for common scenarios. Customize or build your own from scratch. No coding required.' },
    ],
    steps: [
      { num: '01', title: 'Define', desc: 'Pick a trigger (lead created, stage changed, payment due) and an action (assign, send message, update field).' },
      { num: '02', title: 'Activate', desc: 'Toggle your automation on. It runs in the background — 24/7, across all branches.' },
      { num: '03', title: 'Monitor', desc: 'See execution logs for every automation. Know what fired, when, and what it did. Debug with confidence.' },
    ],
    metrics: [
      { value: '24/7', label: 'Always Running' },
      { value: '80%', label: 'Less Manual Work' },
      { value: '0', label: 'Leads Missed' },
    ],
    relatedFeatures: ['lead-management', 'communication', 'admissions-pipeline'],
  },
};

export const FEATURE_SLUGS = Object.keys(FEATURES);

export function getFeature(slug) {
  return FEATURES[slug] || null;
}

export function getRelatedFeatures(slugs) {
  return slugs.map(s => FEATURES[s]).filter(Boolean);
}
